import React from 'react';
import styled from 'styled-components';

import { TaskStatusCard } from './TaskStatusCard';
import {
  selectProcessingTasks,
  useTaskProcessingStore,
} from '@stores/taskProcessing.store';
import type { ProcessingTask } from '@stores/taskProcessing.types';
import { Icon } from '@components/ui/Icon';

export interface TaskStatusListProps {
  matchTask?: (task: ProcessingTask) => boolean;
  emptyState?: React.ReactNode;
  className?: string;
}

const ListContainer = styled.div`
  margin-top: var(--space-xl);
  display: grid;
  gap: var(--space-md);
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
  color: var(--text-muted);
`;

const ListContent = styled.div`
  display: grid;
  gap: var(--space-md);
`;

export function TaskStatusList({
  matchTask,
  emptyState,
  className,
}: TaskStatusListProps) {
  const tasks = useTaskProcessingStore(selectProcessingTasks);
  const filteredTasks = React.useMemo(() => {
    return matchTask ? tasks.filter(matchTask) : tasks;
  }, [tasks, matchTask]);

  if (filteredTasks.length === 0) {
    return emptyState ? (
      <>{emptyState}</>
    ) : (
      <EmptyState aria-live="polite">
        <Icon name="info" size="sm" aria-hidden />
        No hay tareas en progreso.
      </EmptyState>
    );
  }

  return (
    <ListContainer className={className} role="status" aria-live="polite">
      <ListContent>
        {filteredTasks.map((task) => (
          <TaskStatusCard key={task.id} task={task} />
        ))}
      </ListContent>
    </ListContainer>
  );
}

TaskStatusList.displayName = 'TaskStatusList';
